import AppBar from '@mui/material/AppBar'
import Avatar from '@mui/material/Avatar'
import Badge, { BadgeProps } from '@mui/material/Badge'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import { styled } from '@mui/material/styles'
import ToolBar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Grid from '@mui/material/Grid2'
import NotificationsIcon from '@mui/icons-material/Notifications'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { profileRequiredSchema } from 'Components/reusable/DataContext/ValidationSchema'
import BadgeIconButton from 'Components/reusable/IconButtons/BadgeIconButton'
import { useSearchData } from 'Components/General/SearchProvider/SearchProvider'
import { useCountryData, useLogin, useNotifications, useUser } from 'hooks'
import React, { useContext, useRef, useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { CountryCode } from 'types/ids'
import LanguageSelector from '../LanguageSelector'
import NotificationModal from './NotificationModal'
import { userAPI } from 'api/UserAPI'
import ProfileModal from '../ProfileCard/ProfileModal'
import { About } from 'types/profileInterfaces'
import { cvAPI } from 'api/CvAPI'
import { personAPI } from 'api/PersonAPI'
import PasswordModal from './PasswordModal'
import { AxiosError } from 'axios'

interface ToolbarContext {
  itemRef: React.RefObject<HTMLDivElement | null>
}

// The default value's type is a lie here because the default value is
// never actually used! We promise that a value is always provided.
export const toolbarContext = React.createContext<ToolbarContext>({} as ToolbarContext)

const { Provider } = toolbarContext

/**
 * Toolbar provider.
 *
 * @param children
 * @returns Provider.
 * @notExported
 */
export const ToolbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const itemRef = useRef<HTMLDivElement>(null)
  return <Provider value={{ itemRef }}>{children}</Provider>
}

/**
 * Styled Badge component.
 * @notExported
 */
const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: 10,
    top: 8,
  },
}))

/**
 * Top toolbar component.
 *
 * @returns Toolbar component.
 * @notExported
 */
const Toolbar: React.FC = () => {
  const { unreadCount } = useNotifications()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const { t, i18n } = useTranslation()
  const { user, profileImage, ready, setUpdateUser } = useUser()
  const [menuOpen, setMenuOpen] = useState<boolean>(false)
  const [contactModalOpen, setContactModalOpen] = useState<boolean>(false)
  const [notificationModalOpen, setNotificationModalOpen] = useState<boolean>(false)
  const [language, setLanguage] = useState<string>(i18n.language)
  const { resetSearch } = useSearchData()
  const location = useLocation()
  const navigate = useNavigate()
  const { itemRef } = useContext(toolbarContext)
  const { loggedIn, azureToken } = useLogin()
  const [about, setAbout] = useState<About>()
  const [updateAbout, setUpdateAbout] = useState<boolean>(true)
  const [image, setImage] = useState<string>('')
  const { countries } = useCountryData()
  const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false)
  const [canScrollToolbarLeft, setCanScrollToolbarLeft] = useState(false)
  const [canScrollToolbarRight, setCanScrollToolbarRight] = useState(false)

  useEffect(() => {
    if (!itemRef) return

    const element = itemRef.current
    if (!element) return

    const updateScrollButtons = () => {
      setCanScrollToolbarLeft(element.scrollLeft > 0)
      setCanScrollToolbarRight(element.scrollLeft + element.clientWidth < element.scrollWidth - 1)
    }

    updateScrollButtons()
    element.addEventListener('scroll', updateScrollButtons, { passive: true })

    const observer = new MutationObserver(updateScrollButtons)
    observer.observe(element, { childList: true, subtree: true })

    return () => {
      element.removeEventListener('scroll', updateScrollButtons)
      observer.disconnect()
    }
  }, [itemRef])

  const scrollToolbar = (amount: number) => {
    itemRef?.current?.scrollBy({ left: amount, behavior: 'smooth' })
  }

  const canScrollToolbar = canScrollToolbarLeft || canScrollToolbarRight

  useEffect(() => {
    if (!user || !language || !loggedIn || !ready) {
      return
    }

    // Avoid repeating the same language writes/changes on rerenders.
    if (user.language === language && i18n.language === language) {
      return
    }

    const controller = new AbortController()

    ;(async () => {
      try {
        if (user.language !== language) {
          await userAPI.setLanguage(language, controller)
        }

        if (i18n.language !== language) {
          await i18n.changeLanguage(language)
        }
      } catch (error) {
        const err = error as AxiosError
        if (err.code !== 'ERR_CANCELED' && err.name !== 'AbortError' && err.name !== 'CanceledError') {
          throw err
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [language, loggedIn, user, ready, i18n])

  useEffect(() => {
    const controller = new AbortController()

    if (updateAbout && loggedIn) {
      ;(async () => {
        try {
          if (user?.Person) {
            const image = await personAPI.getProfileImage(user?.Person.id, controller)
            setImage(image)
          }
          const aboutData = await cvAPI.getUserAbout(controller)
          if (aboutData) {
            aboutData.CV = aboutData.CVs[0]
            aboutData.CV.organizationId = aboutData.organizationId
            if (!aboutData.country) aboutData.country = 'fi' as CountryCode

            setAbout(aboutData)
            setUpdateAbout(false)
          }
        } catch (error) {
          const err = error as AxiosError
          if (err.code !== 'ERR_CANCELED' && err.name !== 'AbortError' && err.name !== 'CanceledError') {
            throw err
          }
        }
      })()
    }

    return () => {
      controller.abort()
    }
  }, [updateAbout, loggedIn, user?.Person])

  const toggleAccountMenu = (event: React.MouseEvent<HTMLElement>) => {
    // update profile image and about data
    setUpdateAbout(true)
    setAnchorEl(event.currentTarget)
    setMenuOpen(true)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setMenuOpen(false)
  }

  const handleLogout = () => {
    resetSearch()
    navigate('/logout')
    handleClose()
  }

  const changeOldImage = (image: string) => {
    setImage(image)
  }

  let hideToolbar = false
  if (!user || !loggedIn) {
    hideToolbar = true
  } else {
    if (location.pathname.includes('/assignments/public') || location.pathname.includes('/workorders/public')) {
      hideToolbar = true
    } else {
      hideToolbar = false
    }
  }

  const badgeInvisible = useMemo(() => {
    if (user && user.Person?.telephone && user.Person?.telephone.length > 4) {
      return true
    } else {
      return false
    }
  }, [user])

  const notificationsButton = useMemo(() => {
    return (
      <BadgeIconButton
        clickAction={() => navigate('/notifications')}
        icon={
          <Tooltip title={t('notifications')}>
            <NotificationsIcon />
          </Tooltip>
        }
        badgeContent={unreadCount}
      />
    )
  }, [navigate, t, unreadCount])

  return (
    <>
      {!hideToolbar && (
        <AppBar position="static" elevation={4} sx={{ width: '100%' }}>
          <ToolBar
            variant="dense"
            sx={{ display: 'flex', alignItems: 'center', minHeight: '44px', overflow: 'hidden', gap: 1 }}
          >
            <div style={{ display: 'flex', alignItems: 'center', flex: '1 1 auto', minWidth: 0 }}>
              {canScrollToolbar && (
                <IconButton
                  aria-label={t('toolbar.scrollLeft')}
                  onClick={() => scrollToolbar(-240)}
                  disabled={!canScrollToolbarLeft}
                  size="small"
                  sx={{ flex: '0 0 auto' }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              )}
              <Box
                data-testid="toolbarItems"
                ref={itemRef}
                sx={{
                  display: 'flex',
                  flexWrap: 'nowrap',
                  alignItems: 'center',
                  height: '44px',
                  flex: '1 1 auto',
                  minWidth: 0,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              />
              {canScrollToolbar && (
                <IconButton
                  aria-label={t('toolbar.scrollRight')}
                  onClick={() => scrollToolbar(240)}
                  disabled={!canScrollToolbarRight}
                  size="small"
                  sx={{ flex: '0 0 auto' }}
                >
                  <ChevronRightIcon />
                </IconButton>
              )}
            </div>

            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                height: '44px',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '4px',
                flex: '0 0 auto',
              }}
            >
              {loggedIn && user && ready && (
                <span style={{ display: 'flex', alignItems: 'center' }}>{notificationsButton}</span>
              )}
              <Grid sx={{ '& .MuiIconButton-root': { float: 'none' } }}>
                <LanguageSelector setLanguage={setLanguage} global={true} id="toolbar-language" />
              </Grid>
              {loggedIn && user && ready && (
                <span style={{ display: 'flex', alignItems: 'center' }}>
                  <Badge
                    variant="dot"
                    color="error"
                    overlap="circular"
                    invisible={badgeInvisible}
                    sx={{ cursor: 'pointer' }}
                  >
                    <Tooltip title={t('accountMenu')}>
                      <IconButton
                        id="account-menu-button"
                        size="small"
                        onClick={toggleAccountMenu}
                        aria-label={t('accountMenu')}
                        aria-controls={menuOpen ? 'account-menu' : undefined}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen ? 'true' : undefined}
                      >
                        <Avatar src={profileImage} alt={t('accountMenu')} />
                      </IconButton>
                    </Tooltip>
                  </Badge>
                </span>
              )}
              {loggedIn && user && ready && (
                <>
                  <Menu
                    id="account-menu"
                    anchorEl={anchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    keepMounted
                    open={menuOpen}
                    onClose={handleClose}
                  >
                    <StyledBadge variant="dot" color="error" invisible={badgeInvisible}>
                      <MenuItem
                        onClick={() => {
                          setUpdateAbout(true)
                          setContactModalOpen(true)
                        }}
                      >
                        {t('contactInformation')}
                      </MenuItem>
                    </StyledBadge>
                    <MenuItem onClick={() => setNotificationModalOpen(true)}>{t('notificationSettings')}</MenuItem>
                    {!azureToken && (
                      <MenuItem onClick={() => setPasswordModalOpen(true)}>{t('changePassword.header')}</MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>{t('logout')}</MenuItem>
                  </Menu>
                </>
              )}
            </div>
          </ToolBar>
        </AppBar>
      )}
      {contactModalOpen && user && user.Person && about && (
        <ProfileModal
          item={about}
          schema={profileRequiredSchema()}
          maxWidth="md"
          fullWidth
          localeBase={'profile.modal'}
          countries={countries}
          oldImage={image}
          personId={user.Person.id}
          changeOldImage={changeOldImage}
          onClose={() => {
            setContactModalOpen(false)
            setUpdateAbout(true)
            setUpdateUser(true)
            if (location.pathname.includes('/profile')) {
              navigate(0)
            }
          }}
          disableDelete={true}
          submitOnModal={true}
          header="about"
          isPhoneRequired
          noEscClose
        />
      )}
      {notificationModalOpen && (
        <NotificationModal
          onClose={() => setNotificationModalOpen(false)}
          allowSubmit={t('save')}
          header={t('notificationSettings')}
          noEscClose
        />
      )}
      {passwordModalOpen && (
        <PasswordModal onClose={() => setPasswordModalOpen(false)} header={t('changePassword.header')} />
      )}
    </>
  )
}

export default Toolbar
